import { getCookie } from "./cookieUtils";
import { getTagularCookieDomain, isDomCaptureEnabled } from "./tagular-cookie-domain";

/** Matches edge TWO_U_ANON_ID_COOKIE (ajs_anonymous_id). */
const ANON_ID_COOKIE = "ajs_anonymous_id";

let validateAnonIdRan = false;

/** Set once OneTrust has populated active groups for this page view. */
let consentAtLoadCaptured = false;
/** True when C0002 was already active on first observation (US opt-out). */
let c0002AtLoad = false;

function getAnonIdCookie(): string | null {
  const value = getCookie(ANON_ID_COOKIE);
  if (!value) return null;

  try {
    return decodeURIComponent(value.trim());
  } catch {
    return value.trim();
  }
}

/** Segment keeps the pre-consent ID in memory even when Cohesion overwrites the cookie. */
function getSegmentAnonymousId(): string | null {
  if (typeof window === "undefined") return null;

  try {
    const id = window.analytics?.user?.()?.anonymousId?.();
    return id?.trim() || null;
  } catch {
    return null;
  }
}

function setAnonId(id: string, domain: string): void {
  if (!id || !domain) return;

  const expires = new Date();
  expires.setTime(expires.getTime() + 365 * 24 * 60 * 60 * 1000);
  document.cookie = `${ANON_ID_COOKIE}=${encodeURIComponent(id)}; domain=${domain}; path=/; expires=${expires.toUTCString()}; secure; samesite=strict`;
}

function hasPerformanceConsent(): boolean {
  const groups = window.OnetrustActiveGroups || "";
  return groups.indexOf("C0002") !== -1;
}

/**
 * Snapshot whether C0002 was already on when OneTrust first reports groups this page view.
 * Prefers early capture from OneTrustSnippet (`window.__domCaptureConsent`).
 * US opt-out: true → push owns alignment. Non-US opt-in: false → validateAnonId after Accept.
 */
function captureConsentAtLoad(): boolean {
  if (consentAtLoadCaptured) return true;

  const shared = window.__domCaptureConsent;
  if (shared && typeof shared.c0002AtLoad === "boolean") {
    c0002AtLoad = shared.c0002AtLoad;
    consentAtLoadCaptured = true;
    return true;
  }

  const groups = window.OnetrustActiveGroups;
  if (groups === undefined || groups === null || groups === "") return false;

  c0002AtLoad = groups.indexOf("C0002") !== -1;
  consentAtLoadCaptured = true;
  window.__domCaptureConsent = { c0002AtLoad };
  return true;
}

/**
 * US opt-out: C0002 already on at page load → Cohesion minted first.
 * Push that ID into the Segment stub so async Segment adopts X instead of minting Y.
 * No-ops when C0002 was not present at load (non-US) — validateAnonId owns that path.
 */
export function pushCohesionAnonIdToSegment(): void {
  if (typeof window === "undefined" || !isDomCaptureEnabled()) return;
  if (!captureConsentAtLoad() || !c0002AtLoad) return;
  if (!hasPerformanceConsent()) return;

  const id = window._Cohesion?.anonymousId?.trim?.();
  if (!id) return;

  const analytics = window.analytics;
  if (!analytics) return;

  if (typeof analytics.setAnonymousId === "function") {
    analytics.setAnonymousId(id);
  } else if (typeof analytics.push === "function") {
    analytics.push(["setAnonymousId", id]);
  }
}

/**
 * Non-US opt-in: C0002 was not on at page load; after Accept it appears.
 * Align Cohesion + cookie to Segment (or existing cookie). No-ops for US opt-out (c0002AtLoad).
 */
export function validateAnonId(): void {
  if (typeof window === "undefined" || validateAnonIdRan || !isDomCaptureEnabled()) return;
  if (!captureConsentAtLoad() || c0002AtLoad) return;
  if (!hasPerformanceConsent()) return;

  const anonId = getSegmentAnonymousId() ?? getAnonIdCookie();
  if (!anonId) return;

  validateAnonIdRan = true;
  window._Cohesion = { ...window._Cohesion, anonymousId: anonId };
  setAnonId(anonId, getTagularCookieDomain());
}
